import { CellTextAlignment, GridColumnType, GridFieldType, GridPageTarget, IGridColumn, IGridOptions, Sort } from '../../../base/models/grid-options';
import { ColDef, ICellRendererParams, SortDirection } from 'ag-grid-community';
import { ControlType, FetchStatus, IEntity, Placement, Property, PropertyType } from "../../../base/types";
import { DataContextProvider, useDataContext, usePageContext } from "../../page/context";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { ListPageDialog, OffCanvasModel } from '../links';
import React, { FC, useRef, useState } from "react";

import { AgGridReact } from 'ag-grid-react'; // the AG Grid React Component
import { DesignerMode } from '../../types';
import { IPageView } from '../../page/models';
import { Link } from 'react-router-dom';
import { LookupDisplayName } from '../typography/name-lookup';
import { MUIIcon, Spinner } from '../../../widgets';
import { PageLinkHelper } from '../../../base/helpers';
import { Registry } from '../../../base/register';
import clsx from "clsx";
import { formatValue } from '../../helpers';
import { useAuth } from '../../../../modules/auth';
import { useEffect } from "react";
import { useGetProperties } from "../../../base/hooks";
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useGetLookupLabel } from '../typography/name-lookup/useGetLookupLabel';
import { ContactDetails, useLazyGetContactDetailsQuery } from '../../../services/data/table/contactsApi';
import { useGetLookupNames } from '../boarding-pass/properties/useGetLookupNames';

pdfMake.vfs = pdfFonts.vfs;

export const GridComponent: FC<IControlAttributes> = ({
  control, className, children, props, designerMode, ...rest
}) => {
  const { properties } = useGetProperties({ bindings: control.bindings });
  const options: IGridOptions | undefined = properties[Property.GridOptions];
  const enableCSVExport: boolean | undefined = properties[Property.EnableCSVExport];
  const enablePDFExport: boolean | undefined = properties[Property.EnablePDFExport];

  if (designerMode === DesignerMode.Draft) {
    return (
      <div className={className}>
        <Grid options={{ ...options, pagination: true, paginationPageSize: 5 }} enableCSVExport={enableCSVExport} enablePDFExport={enablePDFExport} />
      </div>
    )
  }

  return (
    <Grid options={options} enableCSVExport={enableCSVExport} enablePDFExport={enablePDFExport} />
  )
}

interface IDialogState {
  type: "Offcanvas" | "Dialog"
  show: boolean,
  heading?: string,
  pageLink?: IPageLink,
}
const getHeaderToExport = (gridApi: any) => {
  const columns = gridApi.getColumnDefs();
  return columns.map((column: any) => ({
    text: column.headerName || column.field || '',
    style: 'tableHeader',
  }));
};

const getRowsToExport = async (gridApi: any, getLookupLabel?: any) => {
  const columns = gridApi.getColumnDefs();
  const nodes: any[] = [];
  gridApi.forEachNodeAfterFilterAndSort((node: any) => nodes.push(node));

  const lookupPromises: Promise<any>[] = [];
  const lookupMap: Record<string, any> = {};

  for (const column of columns) {
    if (column.fieldType === GridFieldType.Lookup && getLookupLabel) {
      for (const node of nodes) {
        const value = gridApi.getValue(column.field, node);
        const key = `${column.lookup}_${value}`;
        if (!lookupMap[key]) {
          lookupPromises.push(
            getLookupLabel({ tableLookup: column.lookup, value }).then((label: any) => {
              lookupMap[key] = label;
            })
          );
        }
      }
    }
  }

  await Promise.all(lookupPromises);

  const rowsToExport = nodes.map((node) =>
    columns.map((column: any) => {
      const value =
        column.fieldType === GridFieldType.Lookup
          ? lookupMap[`${column.lookup}_${gridApi.getValue(column.field, node)}`]
          : gridApi.getValue(column.field, node);

      return {
        text: column.displayFormat ? formatValue(value, column.displayFormat) : value ?? '',
        style: 'tableCell',
      };
    })
  );

  return rowsToExport;
};

const useGetNameFromGuid = (guid: string) => {
  const { getLookupLabel } = useGetLookupNames();
  const [name, setName] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (guid) {
      setLoading(true);
      getLookupLabel({ value: guid })
        .then(fetchedName => setName(fetchedName))
        .catch(err => console.error('Error fetching name:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [guid, getLookupLabel]);

  return { name, loading };
};

const getDocument = async (gridApi: any, getLookupLabel?: any, userName?: string, contactDetails?: ContactDetails, programName?: string, batchName?: string) => {
  const columns = gridApi.getColumnDefs();
  const headerRow = getHeaderToExport(gridApi);
  const rows = await getRowsToExport(gridApi, getLookupLabel);
  const currentDate = new Date().toLocaleString();

  return {
    pageOrientation: 'landscape' as 'landscape',
    content: [
      {
        text: 'STUDENT LEDGER',
        style: 'title',
      },
      {
        text: `User ID: ${userName}`,
        style: 'subTitle',
      },
      {
        text: `Date: ${currentDate}`,
      },
      {
        text: `Roll No.: ${contactDetails?.rollNumber}`,
        style: 'subTitle',
      },
      {
        text: `Student Name: ${contactDetails?.fullName}`,
        style: 'subTitle',
      },
      {
        text: `Program: ${programName || contactDetails?.programId}`,
        style: 'subTitle',
      },
      {
        text: `Batch: ${batchName || contactDetails?.batch}`,
        style: 'subTitle',
      },
      {
        table: {
          headerRows: 1,
          widths: Array(columns.length).fill(`${100 / columns.length}%`),
          body: [headerRow, ...rows],
          heights: 15,
        },
      },
    ],
    styles: {
      title: { fontSize: 18, bold: true, marginBottom: 10, alignment: 'center' as 'center' },
      subTitle: { fontSize: 12, marginBottom: 5, alignment: 'left' as 'left' },
      tableHeader: { bold: true, fontSize: 12, color: 'black', fillColor: '#eeeeee', alignment: 'center' as 'center' },
      tableCell: { fontSize: 10, color: 'black', alignment: 'center' as 'center' },
    },
  };
};

export const exportToPDF = async (gridApi: any, getLookupLabel?: any, userName?: string, contactDetails?: ContactDetails, programName?: string, batchName?: string) => {
  const doc = await getDocument(gridApi, getLookupLabel, userName, contactDetails, programName, batchName);
  pdfMake.createPdf(doc).download();
};


const Grid = ({ options, enableCSVExport, enablePDFExport }: { options?: IGridOptions, enableCSVExport?: boolean, enablePDFExport?: boolean }) => {
  const gridApi = useRef<any>(null);
  const { type, result: data, status } = useDataContext();
  const { pageView } = usePageContext()
  const { siteId, user } = useAuth()
  const [gridColumns, setGridColumns] = useState<ColDef[]>([])
  const { getLookupLabel } = useGetLookupLabel()
  const [getContactDetails, { data: contactDetails, isLoading }] = useLazyGetContactDetailsQuery()
  const { name: programName, loading: loadingProgram } = useGetNameFromGuid(contactDetails?.programId || '');
  const { name: batchName, loading: loadingBatch } = useGetNameFromGuid(contactDetails?.batch || '');
  const [dialog, setDialog] = useState<IDialogState>({
    type: "Dialog",
    show: false
  })

  const [isExporting, setIsExporting] = useState(false);
  const isLoadingNames = loadingProgram || loadingBatch || isLoading;

  useEffect(() => {
    if (!user) return;
    getContactDetails({ contactId: user?.memberOf.find((p) => p.entityId)?.entityId ?? "" })

    // eslint-disable-next-line
  }, [getContactDetails])

  useEffect(() => {
    if (!options?.columns) return;

    const gridColumns = [...options.columns]
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map<ColDef>(column => ({
        field: column.field,
        headerName: column.header,
        flex: 1,
        resizable: column.resizable,
        filter: column.filter,
        sortable: column.sortable,
        cellClass: getCellClass(column),
        fieldType: column.fieldType,
        lookup: column.lookup,
        headerClass: getHeaderClass(column),
        sort: getSortClass(column),
        minWidth: getSize(column.minWidth),
        maxWidth: getSize(column.maxWidth),
        width: getSize(column.width),
        valueGetter: (props) => props.data[props.colDef.field!],
        valueFormatter: (props) => {
          if (column.displayFormat)
            return formatValue(props.value, column.displayFormat)
          return props.value
        },
        cellRenderer: (props: ICellRendererParams) => (
          <GridCell
            showDialog={(data) => setDialog(data)}
            column={column}
            props={props}
            pageView={pageView}
            siteId={siteId} />
        ),
        suppressMovable: true
      }))

    setGridColumns(gridColumns)
    // eslint-disable-next-line
  }, [options])

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  const onBtExport = () => {
    if (gridApi.current) {
      gridApi.current.exportDataAsCsv();
    }
  };

  const onBtExportPDF = async () => {
    if (isLoadingNames) {
      return;
    }

    setIsExporting(true);
    if (gridApi.current || user?.userName || programName || batchName || contactDetails)
      await exportToPDF(gridApi.current,
        getLookupLabel,
        user?.userName,
        contactDetails,
        programName,
        batchName
      );
    setIsExporting(false);
  };

  const getSize = (size?: number) => {
    return (size && size > 0) ? size : undefined;
  }

  const getCellClass = (column: IGridColumn) => {
    let alignment = ""
    switch (column.alignment) {
      case CellTextAlignment.Center:
        alignment = "text-center";
        break;
      case CellTextAlignment.Start:
        alignment = "text-start";
        break;
      case CellTextAlignment.End:
        alignment = "text-end";
        break;
    }

    return clsx(column.cellClass, alignment, 'fs-7')
  }

  const getHeaderClass = (column: IGridColumn) => {
    let alignment = ""
    switch (column.alignment) {
      case CellTextAlignment.Center:
        alignment = "text-center";
        break;
      case CellTextAlignment.Start:
        alignment = "text-start";
        break;
      case CellTextAlignment.End:
        alignment = "text-end";
        break;
    }

    return clsx(column.headerClass, alignment, 'fs-7')
  }

  const getSortClass = (column: IGridColumn): SortDirection | undefined => {
    switch (column.sort) {
      case Sort.Ascending:
        return 'asc';
      case Sort.Descending:
        return 'desc';
      case Sort.Default:
        return undefined;
      default:
        return undefined;
    }
  }

  if (status !== FetchStatus.Success || !data || type !== "List") {
    return <></>;
  }

  return (
    <>
      {enableCSVExport === true && <div className='d-flex justify-content-between '>
        <div></div>
        <button
          onClick={onBtExport}
          className='btn btn-secondary btn-sm mb-2 py-1 d-flex gap-1'
        >
          <MUIIcon iconName='FileDownloadOutlined' />
          Export CSV Data
        </button>
      </div>}
      {enablePDFExport === true && <div className='d-flex justify-content-between '>
        <div></div>
        <button
          onClick={onBtExportPDF}
          className='btn btn-secondary btn-sm mb-2 py-1 d-flex gap-1'
          disabled={isExporting}
        >
          {(isExporting && isLoadingNames) ? <Spinner /> : <MUIIcon iconName='FileDownloadOutlined' />}
          Download PDF
        </button>
      </div>}
      <div className={options?.theme || 'ag-theme-alpine'}>
        <AgGridReact
          onGridReady={onGridReady}
          headerHeight={options?.headerHeight || 32}
          rowHeight={options?.rowHeight || 24}
          columnDefs={gridColumns}
          rowData={data.records}
          domLayout="autoHeight"
          pagination={options?.pagination}
          paginationPageSize={options?.paginationPageSize || 10}
        />
      </div>

      {(dialog.type === "Offcanvas" && dialog.pageLink?.pageId) && <OffCanvasModel
        pageLink={dialog.pageLink}
        show={dialog.show}
        heading={dialog.heading}
        placement={Placement.End}
        hideModal={() => setDialog({ show: false, type: "Dialog" })} />}

      {(dialog.type === "Dialog" && dialog.pageLink?.pageId) && <ListPageDialog
        pageLink={dialog.pageLink}
        show={dialog.show}
        hideModal={() => setDialog({ show: false, type: "Dialog" })} />}
    </>
  )
}

const GridCell = ({ props, column, siteId, pageView, showDialog }: {
  props: ICellRendererParams,
  column: IGridColumn,
  siteId?: string,
  pageView?: IPageView,
  showDialog: (data: IDialogState) => void,
}) => {
  const cellValue = props.valueFormatted || props.value;

  const getCellValue = () => {
    if (column.fieldType === GridFieldType.Lookup && column.lookup) {
      return <LookupDisplayName tableLookup={column.lookup} value={cellValue} />
    }

    return <>{cellValue}</>
  }

  if (column.columnType === GridColumnType.PageLink) {
    return <PageLinkCell
      showDialog={showDialog}
      column={column}
      siteId={siteId}
      pageView={pageView}
      data={props.data}
      cellValue={cellValue}>
      {getCellValue()}
    </PageLinkCell>
  }

  if (column.columnType === GridColumnType.DownloadLink) {
    return <DownloadLinkCell
      column={column}
      siteId={siteId}
      data={props.data}
      cellValue={cellValue}>
      {getCellValue()}
    </DownloadLinkCell>
  }

  return <>
    {getCellValue()}
  </>
}

const DownloadLinkCell = ({ column, siteId, data, cellValue, children }: {
  column: IGridColumn,
  cellValue: string,
  siteId?: string,
  data: IEntity,
  children?: React.ReactNode,
}) => {
  const [documentUrl, setDocumentUrl] = useState<string>()

  useEffect(() => {
    if (!column.urlField)
      return;

    let url = data[column.urlField]
    if (url === undefined)
      return;

    if (url.startsWith("https://")) {
      url = new URL(url).pathname;
    }

    setDocumentUrl(url);
    // eslint-disable-next-line
  }, [])

  if (!documentUrl) {
    return <>{children}</>
  }

  return <>
    <Link
      target='_blank'
      to={`/api/documents/document?url=${encodeURIComponent(documentUrl)}`}>
      {children}
    </Link>
  </>
}

const PageLinkCell = ({ column, siteId, pageView, data, cellValue, children, showDialog }: {
  column: IGridColumn,
  cellValue: string,
  siteId?: string,
  pageView?: IPageView,
  data: IEntity,
  children?: React.ReactNode,
  showDialog: (data: IDialogState) => void,
}) => {
  const [pageLink, setPageLink] = useState<IPageLink>()
  const [pageUrl, setPageUrl] = useState<string>()

  useEffect(() => {
    if (column.columnType !== GridColumnType.PageLink || !column.pageLink || !siteId)
      return

    const filters = [...(pageView?.linkFilters || []), ...(pageView?.viewFilters || [])];
    const newPageLink = PageLinkHelper.resolvePageLink(column.pageLink, data, filters)
    const pageUrl = PageLinkHelper.getPageLinkUrl(siteId, newPageLink)

    setPageLink(newPageLink)
    setPageUrl(pageUrl);
    // eslint-disable-next-line
  }, [])

  const getPageLinkComponent = () => {
    switch (column.pageTarget) {
      case GridPageTarget.Redirect:
        return <Link
          to={(pageUrl) ? pageUrl : '#'}>
          {children}
        </Link>
      case GridPageTarget.NewTab:
        return <Link
          target='_blank'
          to={(pageUrl) ? pageUrl : '#'}>
          {children}
        </Link>
      case GridPageTarget.Dialog:
        return <PageLinkDialogButton
          showDialog={showDialog}
          column={column}
          label={cellValue}
          pageLink={pageLink}>
          {children}
        </PageLinkDialogButton>
      case GridPageTarget.Offcanvas:
        return <PageLinkOffcanvasButton
          showDialog={showDialog}
          column={column}
          heading={cellValue}
          pageLink={pageLink} >
          {children}
        </PageLinkOffcanvasButton>
      default:
        return <>{children}</>
    }
  }

  return <DataContextProvider>
    <SetContextRecord data={data} />
    {getPageLinkComponent()}
  </DataContextProvider>
}

const SetContextRecord = ({ data }: { data: IEntity }) => {
  const { setData } = useDataContext()

  useEffect(() => {
    setData({ type: "Card", result: data, status: FetchStatus.Success })
    // eslint-disable-next-line
  }, [])

  return <></>
}

const PageLinkDialogButton = ({ label, column, pageLink, children, showDialog }: {
  label: string,
  column: IGridColumn,
  pageLink?: IPageLink
  children?: React.ReactNode,
  showDialog: (data: IDialogState) => void
}) => {
  return <>
    <button
      type="button"
      className={clsx(column.cellClass, 'btn btn-link text-primary text-hover-info py-0 fs-7')}
      disabled={!pageLink}
      onClick={() => showDialog({
        show: true,
        type: "Dialog",
        pageLink
      })}>
      {children}
    </button>
  </>
}

const PageLinkOffcanvasButton = ({ heading, column, pageLink, children, showDialog }: {
  heading: string,
  column: IGridColumn,
  pageLink?: IPageLink,
  children?: React.ReactNode
  showDialog: (data: IDialogState) => void,
}) => {
  return <>
    <button
      type="button"
      className={clsx(column.cellClass, 'btn btn-link text-primary text-hover-info py-0 fs-7')}
      disabled={!pageLink}
      onClick={() => showDialog({
        show: true,
        type: "Offcanvas",
        heading,
        pageLink
      })}>
      {children}
    </button>
  </>
}

Registry.addControl({
  id: "34543928-f3db-4456-9f24-b37193fw3678",
  name: "Grid",
  type: ControlType.Grid,
  properties: [
    { id: Property.GridOptions, name: 'Grid Options', type: PropertyType.GridOptions },
    { id: Property.EnableCSVExport, name: 'Enable CSV Export', type: PropertyType.Boolean },
    { id: Property.EnablePDFExport, name: 'Enable PDF Export', type: PropertyType.Boolean },
  ],
  component: GridComponent
})
